import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Upload, Trash2, CheckCircle2, XCircle, TrendingUp, TrendingDown, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, apiGet } from './ops_helpers';

const fmt = (n, d = 2) =>
  (n === null || n === undefined) ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maxFractionDigits: d });
const fmtUSD = (n) => (n === null || n === undefined) ? '—' : `${n >= 0 ? '' : '-'}$${Math.abs(n).toFixed(2)}`;

const CHECK_LABELS = {
  pf_ok: 'PF ≥ 1.3',
  expectancy_ok: 'Expectancy > 0',
  trades_ok: 'Trades ≥ 100',
  dir_balanced: 'BUY/SELL equilibrado',
  not_concentrated: 'No depende de 1 sesión/día',
};

const Verdict = ({ passed }) => (
  <Badge className={passed
    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    : 'bg-rose-500/20 text-rose-300 border-rose-500/30'}>
    {passed ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
    {passed ? 'APRUEBA' : 'NO APRUEBA'}
  </Badge>
);

const StatCell = ({ label, value, tone }) => (
  <div className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className={`text-sm font-semibold ${tone || 'text-zinc-100'}`}>{value}</div>
  </div>
);

const MiniBars = ({ rows, unit = '' }) => {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.net)));
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={String(r.key)} className="flex items-center gap-2 text-xs" data-testid={`bar-${r.key}`}>
          <span className="w-10 shrink-0 text-zinc-400 text-right">{r.key}{unit}</span>
          <div className="flex-1 h-4 bg-zinc-900 rounded relative overflow-hidden">
            <div
              className={`h-full ${r.net >= 0 ? 'bg-emerald-500/50' : 'bg-rose-500/50'}`}
              style={{ width: `${(Math.abs(r.net) / max) * 100}%` }}
            />
          </div>
          <span className={`w-20 shrink-0 text-right tabular-nums ${r.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {fmtUSD(r.net)}
          </span>
          <span className="w-12 shrink-0 text-right text-zinc-500">{r.trades}t</span>
        </div>
      ))}
    </div>
  );
};

const ScenarioCard = ({ s }) => {
  const pfDisplay = s.pf_inf ? '∞' : fmt(s.profit_factor);
  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid={`scenario-card-${s.scenario}`}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <span className="text-yellow-400">●</span> {s.scenario}
        </CardTitle>
        <Verdict passed={s.passed} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatCell label="Net" value={fmtUSD(s.net_sum)} tone={s.net_sum >= 0 ? 'text-emerald-400' : 'text-rose-400'} />
          <StatCell label="Profit Factor" value={pfDisplay} tone={(s.pf_inf || s.profit_factor >= 1.3) ? 'text-emerald-400' : 'text-rose-400'} />
          <StatCell label="Expectancy" value={fmtUSD(s.expectancy)} tone={s.expectancy > 0 ? 'text-emerald-400' : 'text-rose-400'} />
          <StatCell label="Trades" value={s.trades} tone={s.trades >= 100 ? 'text-emerald-400' : 'text-amber-400'} />
          <StatCell label="Win rate" value={`${fmt(s.win_rate, 1)}%`} />
          <StatCell label="Max DD" value={fmtUSD(s.max_drawdown_usd)} tone="text-amber-300" />
          <StatCell label="Comisiones" value={fmtUSD(s.fees_sum)} tone="text-zinc-400" />
          <StatCell label="Bruto" value={fmtUSD(s.gross_sum)} tone="text-zinc-400" />
        </div>

        {/* Checks */}
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(s.checks).map(([k, ok]) => (
            <span key={k} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] border ${ok ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10' : 'border-rose-500/30 text-rose-300 bg-rose-500/10'}`} data-testid={`check-${s.scenario}-${k}`}>
              {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {CHECK_LABELS[k] || k}
            </span>
          ))}
        </div>

        {/* BUY / SELL */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-zinc-800 p-2">
            <div className="flex items-center gap-1 text-xs text-emerald-400 mb-1"><TrendingUp className="w-3.5 h-3.5" /> BUY (long)</div>
            <div className={`text-sm font-semibold ${s.long_net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtUSD(s.long_net)}</div>
            <div className="text-[11px] text-zinc-500">{s.long_trades} trades</div>
          </div>
          <div className="rounded-md border border-zinc-800 p-2">
            <div className="flex items-center gap-1 text-xs text-rose-400 mb-1"><TrendingDown className="w-3.5 h-3.5" /> SELL (short)</div>
            <div className={`text-sm font-semibold ${s.short_net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtUSD(s.short_net)}</div>
            <div className="text-[11px] text-zinc-500">{s.short_trades} trades</div>
          </div>
        </div>

        {/* Desgloses hora / dia */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-zinc-400 mb-1.5">Net por hora (UTC) · conc. {fmt(s.concentration_hour_pct, 1)}%</div>
            <MiniBars rows={s.by_hour} unit="h" />
          </div>
          <div>
            <div className="text-xs text-zinc-400 mb-1.5">Net por día · conc. {fmt(s.concentration_dow_pct, 1)}%</div>
            <MiniBars rows={s.by_dow} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const SniperV2StressLab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      const d = await apiGet('/api/ops/sniper-v2/stress/latest');
      if (d.exists) setData(d);
    } catch (e) { /* sin datos aun */ }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/ops/sniper-v2/stress/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || res.statusText); }
      const d = await res.json();
      setData(d);
      toast.success(`Analizados ${d.total_trades} trades · ${d.scenarios.length} escenario(s)`);
    } catch (e) { toast.error(`Error al subir: ${e.message}`); }
    finally { setLoading(false); ev.target.value = ''; }
  };

  const onReset = async () => {
    if (!window.confirm('¿Resetear el Sniper v2 Stress Lab? Se borran todos los análisis subidos.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ops/sniper-v2/stress`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.statusText);
      setData(null);
      toast.success('Stress Lab reseteado. Listo para empezar de cero.');
    } catch (e) { toast.error(`Error al resetear: ${e.message}`); }
  };

  return (
    <div className="space-y-4" data-testid="sniper-v2-stress-lab">
      {/* Upload / reset */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-zinc-200">Stress test de costes (CSV por-operación)</p>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
              Sube <code className="text-yellow-400">Sniper_v2_trades_breakdown.csv</code> (uno o varios escenarios: real / spread_x1.5 / spread_x2 / oos).
              Calculo net/PF/expectancy/DD tras costes, desglose BUY-SELL / hora / día, y el veredicto de aprobación.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && (
              <Button variant="outline" size="sm" onClick={onReset} className="border-rose-700 text-rose-300 hover:bg-rose-500/10" data-testid="stress-reset-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Resetear
              </Button>
            )}
            <label>
              <input type="file" accept=".csv" className="hidden" onChange={onUpload} data-testid="stress-upload-input" disabled={loading} />
              <span className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer ${loading ? 'bg-zinc-700 text-zinc-400' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}`} data-testid="stress-upload-btn">
                <Upload className="w-4 h-4" /> {loading ? 'Procesando…' : 'Subir CSV'}
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {!data ? (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500 text-sm" data-testid="stress-empty">
            Aún no hay stress test. Corre los backtests del Sniper v2 en MT5 con su <code className="text-yellow-400">Inp_ScenarioTag</code> (real / spread_x1.5 / spread_x2)
            y sube el <code className="text-yellow-400">Sniper_v2_trades_breakdown.csv</code> que genera (en <code>MQL5\\Files</code>).
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Veredicto global */}
          <Card className={`border ${data.all_scenarios_pass ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-rose-500/5 border-rose-500/30'}`} data-testid="stress-global-verdict">
            <CardContent className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {data.all_scenarios_pass
                  ? <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  : <AlertTriangle className="w-8 h-8 text-rose-400" />}
                <div>
                  <p className="text-base font-semibold text-zinc-100">
                    {data.all_scenarios_pass ? 'Todos los escenarios APRUEBAN' : 'Aún NO listo para real'}
                  </p>
                  <p className="text-xs text-zinc-400">
                    {data.total_trades} trades · {data.scenarios.join(', ')}
                    {data.survives_x15 !== null && (
                      <> · Sobrevive spread ×1.5: <strong className={data.survives_x15 ? 'text-emerald-400' : 'text-rose-400'}>{data.survives_x15 ? 'SÍ' : 'NO'}</strong></>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 text-right">
                Criterios: PF≥{data.criteria.pf_min} · Trades≥{data.criteria.trades_min} · Concentración≤{data.criteria.max_concentration_pct}%
              </div>
            </CardContent>
          </Card>

          {/* Comparativa rapida */}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Comparativa por escenario</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-500 text-xs">Escenario</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Net</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">PF</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Expectancy</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Trades</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">WR</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Max DD</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-center">Veredicto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.per_scenario.map((s) => (
                    <TableRow key={s.scenario} className="border-zinc-800" data-testid={`compare-row-${s.scenario}`}>
                      <TableCell className="text-sm text-zinc-200">{s.scenario}</TableCell>
                      <TableCell className={`text-right tabular-nums ${s.net_sum >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtUSD(s.net_sum)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${(s.pf_inf || s.profit_factor >= 1.3) ? 'text-emerald-400' : 'text-rose-400'}`}>{s.pf_inf ? '∞' : fmt(s.profit_factor)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${s.expectancy > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtUSD(s.expectancy)}</TableCell>
                      <TableCell className="text-right tabular-nums text-zinc-300">{s.trades}</TableCell>
                      <TableCell className="text-right tabular-nums text-zinc-300">{fmt(s.win_rate, 1)}%</TableCell>
                      <TableCell className="text-right tabular-nums text-amber-300">{fmtUSD(s.max_drawdown_usd)}</TableCell>
                      <TableCell className="text-center"><Verdict passed={s.passed} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Detalle por escenario */}
          <div className="grid grid-cols-1 gap-4">
            {data.per_scenario.map((s) => <ScenarioCard key={s.scenario} s={s} />)}
          </div>
        </>
      )}
    </div>
  );
};

export default SniperV2StressLab;
